import React, { useEffect, useState, useMemo, useRef } from 'react';
import { RotateCcw, AlertCircle, Eye, Search, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getAllBgvCases, getClientList, getActiveEmployees } from '../../../services/admin/api';
import './allBgvCasesPage.css';

const AllBgvCasesPage = () => {
  const navigate = useNavigate();

  const [rawCases, setRawCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [clientsMaster, setClientsMaster] = useState([]);
  const [employeesMaster, setEmployeesMaster] = useState([]);

  // Dropdown visibility states
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showCheckTypeDropdown, setShowCheckTypeDropdown] = useState(false);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);

  // Filter criteria states
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedCheckTypes, setSelectedCheckTypes] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [searchTerm, setSearchTerm] = useState('');
  
  // Search inputs inside dropdown filters (For Criteria 3 & 6)
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');

  // Sorting states
  const [sortColumn, setSortColumn] = useState('caseId');
  const [sortDirection, setSortDirection] = useState('desc');

  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Refs for tracking click outside overlays
  const statusRef = useRef(null);
  const checkTypeRef = useRef(null);
  const clientRef = useRef(null);
  const employeeRef = useRef(null);

  useEffect(() => {
    const initializeDashboardCache = async () => {
      try {
        setLoading(true);
        const [casesRes, clientsData, employeesRes] = await Promise.all([
          getAllBgvCases().catch((err) => {
            console.error("BGV Cases API failed:", err);
            toast.error("Could not fetch verification cases from the server.");
            return { status: false, data: [] };
          }),
          getClientList().catch(() => []),
          getActiveEmployees(0, 5000).catch(() => ({ content: [] }))
        ]);

        if (casesRes && casesRes.status && Array.isArray(casesRes.data)) {
          setRawCases(casesRes.data);
        } else {
          setRawCases([]);
        }

        setClientsMaster(clientsData || []);
        setEmployeesMaster(employeesRes?.content || []);
      } catch (err) {
        console.error("Cache initialization failed", err);
        setError("Failed to load complete verification case log dataset.");
      } finally {
        setLoading(false);
      }
    };

    initializeDashboardCache();

    const handleClickOutside = (event) => {
      if (statusRef.current && !statusRef.current.contains(event.target)) setShowStatusDropdown(false);
      if (checkTypeRef.current && !checkTypeRef.current.contains(event.target)) setShowCheckTypeDropdown(false);
      if (clientRef.current && !clientRef.current.contains(event.target)) setShowClientDropdown(false);
      if (employeeRef.current && !employeeRef.current.contains(event.target)) setShowEmployeeDropdown(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSortToggle = (columnKey) => {
    if (sortColumn === columnKey) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const renderSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) return <span className="bgv-sort-arrow-inactive"> ↕</span>;
    return sortDirection === 'asc' ? <span className="bgv-sort-arrow-active"> ▴</span> : <span className="bgv-sort-arrow-active"> ▾</span>;
  };

  const handleCheckboxToggle = (value, stateArray, setterFunction) => {
    if (stateArray.includes(value)) {
      setterFunction(stateArray.filter(item => item !== value));
    } else {
      setterFunction([...stateArray, value]);
    }
    setCurrentPage(1);
  };

  const handleDateChange = (field, value) => {
    setDateRange(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'endDate' && updated.startDate && value < updated.startDate) {
        toast.error("End Date cannot be configured earlier than selected Start Date.");
        return prev;
      }
      return updated;
    });
    setCurrentPage(1);
  };

  const handleResetAllFilters = () => {
    setSelectedStatuses([]);
    setSelectedClient('');
    setSelectedCheckTypes([]);
    setSelectedEmployee('');
    setDateRange({ startDate: '', endDate: '' });
    setSearchTerm('');
    setClientSearchQuery('');
    setEmployeeSearchQuery('');
    setCurrentPage(1);
    toast.info("Filter selection properties cleared.");
  };

  // Extracts dynamic options straight from state cache
  const dynamicUniqueOptions = useMemo(() => {
    return {
      clients: [...new Set(rawCases.map(c => c.clientName).filter(Boolean))],
      employees: [...new Set(rawCases.map(c => c.assignedEmployee).filter(name => name && name !== "Not Assigned Yet"))],
      checkTypes: [...new Set(rawCases.map(c => c.checkType ? c.checkType.trim().toUpperCase() : "PERSONAL_DETAILS"))]
    };
  }, [rawCases]);

  // Filtering dropdown values based on user inner queries
  const filteredClientOptions = useMemo(() => {
    return dynamicUniqueOptions.clients.filter(client =>
      client.toLowerCase().includes(clientSearchQuery.toLowerCase())
    );
  }, [dynamicUniqueOptions.clients, clientSearchQuery]);

  const filteredEmployeeOptions = useMemo(() => {
    return dynamicUniqueOptions.employees.filter(emp =>
      emp.toLowerCase().includes(employeeSearchQuery.toLowerCase())
    );
  }, [dynamicUniqueOptions.employees, employeeSearchQuery]);

  // Combined Filtration Pipeline
  const filteredCasesList = useMemo(() => {
    return rawCases.map(item => {
      return {
        ...item,
        clientName: item.clientName || "Unknown Client",
        assignedEmployee: item.assignedEmployee || "Not Assigned Yet"
      };
    }).filter(item => {
      // 1. Status Filter (Multi-select)
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(item.status)) return false;
      
      // 2. Client Filter
      if (selectedClient && item.clientName !== selectedClient) return false;

      // 3. Check Type Filter (Multi-select)
      if (selectedCheckTypes.length > 0) {
        const itemCheckType = item.checkType ? item.checkType.trim().toUpperCase().replace(/\s+/g, '_') : "PERSONAL_DETAILS";
        if (!selectedCheckTypes.includes(itemCheckType)) return false;
      }

      // 4. Employee Filter
      if (selectedEmployee && item.assignedEmployee !== selectedEmployee) return false;

      // 5. Global Search Box Filter
      if (searchTerm.trim() !== '') {
        const lowerSearch = searchTerm.toLowerCase();
        
        // DEFINED CORRECTLY: Declaring all match properties explicitly inside this block scope
        const matchesCandidateId = (item.candidateId || '').toLowerCase().includes(lowerSearch);
        const matchesCandidateName = (item.candidateName || '').toLowerCase().includes(lowerSearch);
        const matchesClient = (item.clientName || '').toLowerCase().includes(lowerSearch);
        const matchesEmployee = (item.assignedEmployee || '').toLowerCase().includes(lowerSearch);

        // If none of the fields match the global search query, exclude the row item
        if (!matchesCandidateId && !matchesCandidateName && !matchesClient && !matchesEmployee) {
          return false;
        }
      }

      // 6. Optional Date Range Filter ( FIXED: Criteria 1 fulfilled, items display immediately on page load!)
      if (dateRange.startDate && dateRange.endDate) {
        const startTime = new Date(dateRange.startDate).setHours(0, 0, 0, 0);
        const endTime = new Date(dateRange.endDate).setHours(23, 59, 59, 999);
        if (!item.caseCreatedDate) return false;
        const itemTime = new Date(item.caseCreatedDate).getTime();

        if (itemTime < startTime || itemTime > endTime) return false;
      }

      return true;
    }).sort((a, b) => {
      let valA = a[sortColumn];
      let valB = b[sortColumn];
      if (sortColumn === 'caseCreatedDate') {
        valA = valA ? new Date(valA).getTime() : 0;
        valB = valB ? new Date(valB).getTime() : 0;
      }

      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();

      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rawCases, selectedStatuses, selectedClient, selectedCheckTypes, selectedEmployee, dateRange, searchTerm, sortColumn, sortDirection]);

  const paginatedDataRows = useMemo(() => {
    const startIndex = (currentPage - 1) * recordsPerPage;
    return filteredCasesList.slice(startIndex, startIndex + recordsPerPage);
  }, [filteredCasesList, currentPage, recordsPerPage]);

  const totalPagesCount = Math.ceil(filteredCasesList.length / recordsPerPage);

  const paginationPageNumbers = useMemo(() => {
    let startPage = Math.max(1, currentPage - 1);
    let endPage = Math.min(totalPagesCount, startPage + 2);

    if (endPage - startPage < 2) startPage = Math.max(1, endPage - 2);

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      if (i >= 1) pages.push(i);
    }
    return pages;
  }, [currentPage, totalPagesCount]);

  if (loading) {
    return (
      <div className="bgv-dashboard-centered-loader">
        <div className="bgv-loader-card">
          <div className="bgv-dashboard-spinner"></div>
          <h3 className="bgv-loader-title">Loading Dashboard</h3>
          <p className="bgv-loader-text">Assembling background verification data cache...</p>
        </div>
      </div>
    );
  }

  if (error) return <div className="bgv-dashboard-error-banner">{error}</div>;

  return (
  <div className="bgv-cases-dashboard-main-wrapper">

    {/* Back Button */}
    <div className="bgv-back-button-wrapper">
      <button
        type="button"
        className="bgv-back-button"
         onClick={() => navigate(-1)}
      >
        <ArrowLeft size={18} />
        <span>Back</span>
      </button>
    </div>

    <div className="bgv-dashboard-header-container">
      <h1 className="bgv-dashboard-page-main-title">
        All BGV Cases List
      </h1>
    </div>
      <div className="bgv-filter-panel-card-shell">
        <div className="bgv-filter-grid-layout">
          
          {/* Status Multi-Select */}
          <div className="bgv-filter-dropdown-input-group" ref={statusRef}>
            <label>Case Status</label>
            <div className="bgv-custom-multiselect-anchor" onClick={() => { setShowStatusDropdown(!showStatusDropdown); setShowCheckTypeDropdown(false); setShowClientDropdown(false); setShowEmployeeDropdown(false); }}>
              <div className="bgv-multiselect-placeholder-box">
                {selectedStatuses.length === 0 ? "Select Statuses" : `Selected (${selectedStatuses.length})`}
              </div>
              <span className="bgv-dropdown-carat-arrow">▼</span>
            </div>
            {showStatusDropdown && (
              <div className="bgv-multiselect-floating-overlay-card">
                {["PENDING", "APPROVED", "REJECTED"].map(st => (
                  <label key={st} className="bgv-checkbox-option-row">
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes(st)}
                      onChange={() => handleCheckboxToggle(st, selectedStatuses, setSelectedStatuses)}
                    />
                    <span>{st}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/*  CRITERIA 3 FIXED: Searchable Client Dropdown Wrapper */}
          <div className="bgv-filter-dropdown-input-group" ref={clientRef}>
            <label>Client Corporate Name</label>
            <div className="bgv-custom-multiselect-anchor" onClick={() => { setShowClientDropdown(!showClientDropdown); setShowStatusDropdown(false); setShowCheckTypeDropdown(false); setShowEmployeeDropdown(false); }}>
              <div className="bgv-multiselect-placeholder-box">
                {selectedClient || "All Registered Clients"}
              </div>
              <span className="bgv-dropdown-carat-arrow">▼</span>
            </div>
            {showClientDropdown && (
              <div className="bgv-multiselect-floating-overlay-card dynamic-searchable-dropdown">
                <div className="dropdown-search-input-container">
                  <Search size={14} className="search-icon-inside" />
                  <input 
                    type="text" 
                    placeholder="Search client..." 
                    value={clientSearchQuery}
                    onChange={(e) => setClientSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className="dropdown-options-scrollable-zone">
                  <div className="dropdown-selectable-row-item" onClick={() => { setSelectedClient(''); setShowClientDropdown(false); setClientSearchQuery(''); setCurrentPage(1); }}>
                    All Registered Clients
                  </div>
                  {filteredClientOptions.map(cl => (
                    <div key={cl} className={`dropdown-selectable-row-item ${selectedClient === cl ? 'selected' : ''}`} onClick={() => { setSelectedClient(cl); setShowClientDropdown(false); setClientSearchQuery(''); setCurrentPage(1); }}>
                      {cl}
                    </div>
                  ))}
                  {filteredClientOptions.length === 0 && <div className="dropdown-empty-notice">No clients found</div>}
                </div>
              </div>
            )}
          </div>

          {/* Check Type Multi-Select */}
          <div className="bgv-filter-dropdown-input-group" ref={checkTypeRef}>
            <label>Verification Check Type</label>
            <div className="bgv-custom-multiselect-anchor" onClick={() => { setShowCheckTypeDropdown(!showCheckTypeDropdown); setShowStatusDropdown(false); setShowClientDropdown(false); setShowEmployeeDropdown(false); }}>
              <div className="bgv-multiselect-placeholder-box">
                {selectedCheckTypes.length === 0 ? "Select Check Types" : `Selected (${selectedCheckTypes.length})`}
              </div>
              <span className="bgv-dropdown-carat-arrow">▼</span>
            </div>
            {showCheckTypeDropdown && (
              <div className="bgv-multiselect-floating-overlay-card">
                {dynamicUniqueOptions.checkTypes.length === 0 ? (
                  <div className="bgv-dropdown-empty-info" style={{ padding: '8px 12px', fontSize: '0.85rem', color: '#64748b' }}>No active check types found</div>
                ) : (
                  dynamicUniqueOptions.checkTypes.map(ct => (
                    <label key={ct} className="bgv-checkbox-option-row">
                      <input
                        type="checkbox"
                        checked={selectedCheckTypes.includes(ct)}
                        onChange={() => handleCheckboxToggle(ct, selectedCheckTypes, setSelectedCheckTypes)}
                      />
                      <span>{ct.replace(/_/g, ' ')}</span>
                    </label>
                  ))
                )}
              </div>
            )}
          </div>

          {/*  CRITERIA 6 FIXED: Searchable Employee Dropdown Wrapper */}
          <div className="bgv-filter-dropdown-input-group" ref={employeeRef}>
            <label>Assigned Verification Agent</label>
            <div className="bgv-custom-multiselect-anchor" onClick={() => { setShowEmployeeDropdown(!showEmployeeDropdown); setShowStatusDropdown(false); setShowCheckTypeDropdown(false); setShowClientDropdown(false); }}>
              <div className="bgv-multiselect-placeholder-box">
                {selectedEmployee || "All Active Employees"}
              </div>
              <span className="bgv-dropdown-carat-arrow">▼</span>
            </div>
            {showEmployeeDropdown && (
              <div className="bgv-multiselect-floating-overlay-card dynamic-searchable-dropdown">
                <div className="dropdown-search-input-container">
                  <Search size={14} className="search-icon-inside" />
                  <input 
                    type="text" 
                    placeholder="Search agent..." 
                    value={employeeSearchQuery}
                    onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className="dropdown-options-scrollable-zone">
                  <div className="dropdown-selectable-row-item" onClick={() => { setSelectedEmployee(''); setShowEmployeeDropdown(false); setEmployeeSearchQuery(''); setCurrentPage(1); }}>
                    All Active Employees
                  </div>
                  <div className="dropdown-selectable-row-item" onClick={() => { setSelectedEmployee('Not Assigned Yet'); setShowEmployeeDropdown(false); setEmployeeSearchQuery(''); setCurrentPage(1); }}>
                    Not Assigned Yet
                  </div>
                  {filteredEmployeeOptions.map(emp => (
                    <div key={emp} className={`dropdown-selectable-row-item ${selectedEmployee === emp ? 'selected' : ''}`} onClick={() => { setSelectedEmployee(emp); setShowEmployeeDropdown(false); setEmployeeSearchQuery(''); setCurrentPage(1); }}>
                      {emp}
                    </div>
                  ))}
                  {filteredEmployeeOptions.length === 0 && <div className="dropdown-empty-notice">No agents found</div>}
                </div>
              </div>
            )}
          </div>

          {/* Date Picker Range Box */}
          <div className="bgv-filter-dropdown-input-group bgv-date-picker-range-box-wrapper">
            <label className="bgv-highlight-date-label">Case Creation Date Range</label>
            <div className="bgv-date-inputs-flex-row intense-border">
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => handleDateChange('startDate', e.target.value)}
              />
              <span className="bgv-range-join-dash">to</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => handleDateChange('endDate', e.target.value)}
              />
            </div>
          </div>

          <div className="bgv-filter-action-button-alignment-wrapper">
            <button className="bgv-dashboard-reset-action-btn" onClick={handleResetAllFilters}>
              <RotateCcw size={15} />
              <span>Reset Filters</span>
            </button>
          </div>

        </div>
      </div>

      {/*  CRITERIA 10 FIXED: Fallback message triggers dynamically when results hit 0 */}
      {filteredCasesList.length === 0 ? (
        <div className="bgv-dashboard-empty-state-alert-box">
          <AlertCircle size={44} className="bgv-empty-state-alert-icon" />
          <h3>No cases found for selected criteria</h3>
          <p>Adjust your filter properties or clear them out completely to re-index rows.</p>
          <button className="bgv-empty-clear-action-btn" onClick={handleResetAllFilters}>
            <RotateCcw size={15} />
            <span>Clear Active Filters</span>
          </button>
        </div>
      ) : (
        <>
          <div className="bgv-search-bar-container">
            <div className="bgv-search-input-wrapper">
              <input
                type="text"
                placeholder="Search cases instantly..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              />
              {searchTerm && (
                <button className="bgv-search-clear-btn" onClick={() => setSearchTerm('')}>
                  ✕
                </button>
              )}
            </div>
          </div>
          
          <div className="bgv-table-layout-row-metadata-controls-bar">
            <div className="bgv-page-entries-selector-box">
              <span>Show</span>
              <select value={recordsPerPage} onChange={(e) => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
            <div className="bgv-live-counter-badge-display">
              Filtered Results Match Output: <strong>{filteredCasesList.length}</strong> cases found
            </div>
          </div>

          <div className="bgv-responsive-table-scroll-container-card">
            <table className="bgv-dashboard-data-table-engine">
              <thead>
                <tr>
                  <th onClick={() => handleSortToggle('caseId')} className="sortable-header text-center">
                    Case ID {renderSortIcon('caseId')}
                  </th>
                  {/*  FIXED: Changed alignment class to align with your ID badges */}
                  <th onClick={() => handleSortToggle('candidateId')} className="sortable-header text-left">
                    Candidate ID {renderSortIcon('candidateId')}
                  </th>
                  <th onClick={() => handleSortToggle('clientName')} className="sortable-header text-left">
                    Client Name {renderSortIcon('clientName')}
                  </th>
                  <th onClick={() => handleSortToggle('checkType')} className="sortable-header text-left">
                    Check Type {renderSortIcon('checkType')}
                  </th>
                  <th onClick={() => handleSortToggle('status')} className="sortable-header text-center">
                    Status {renderSortIcon('status')}
                  </th>
                  <th onClick={() => handleSortToggle('assignedEmployee')} className="sortable-header text-left">
                    Assigned Employee {renderSortIcon('assignedEmployee')}
                  </th>
                  <th onClick={() => handleSortToggle('caseCreatedDate')} className="sortable-header text-center">
                    Case Created Date {renderSortIcon('caseCreatedDate')}
                  </th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedDataRows.map((row) => (
                  <tr key={row.caseId} className="bgv-data-table-body-row-hover-animation">
                    <td className="text-center" style={{ fontWeight: '500' }}>{row.caseId}</td>
                    
                    {/*  FIXED: Removed the inline style padding push and set text-left */}
                    <td className="text-left">
                      <div className="bgv-identity-meta-cell">
                        <span className="bgv-candidate-text-name" style={{ fontWeight: '600', color: '#1a1a1a' }}>
                          {row.candidateId}
                        </span>
                        <span className="bgv-candidate-id-badge-subtext" style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>
                          {row.candidateName}
                        </span>
                      </div>
                    </td>
                    
                    <td className="text-left">{row.clientName}</td>
                    <td className="text-left">{row.checkType ? row.checkType.replace(/_/g, ' ') : "PERSONAL DETAILS"}</td>
                    <td className="text-center">
                      <span className={`bgv-status-badge-indicator-pill ${row.status ? row.status.toLowerCase() : 'pending'}`}>
                        {row.status}
                      </span>
                    </td>
                    <td className="text-left">{row.assignedEmployee}</td>
                    <td className="text-center">{row.caseCreatedDate ? new Date(row.caseCreatedDate).toLocaleDateString('en-GB') : "N/A"}</td>
                    <td className="text-center">
                      <button
                        className="bgv-action-row-view-details-icon-btn"
                        onClick={() => navigate(`/admin/candidate-details/${row.candidateId}`)}
                      >
                        <Eye size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bgv-footer-pagination-navigation-controls-bar">
            <div className="bgv-pagination-summary-text-label">
              Showing {(currentPage - 1) * recordsPerPage + 1} to {Math.min(currentPage * recordsPerPage, filteredCasesList.length)} of {filteredCasesList.length} entries
            </div>
            <div className="bgv-pagination-action-buttons-flex-group">
              <button
                className="bgv-page-nav-action-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              >
                Previous
              </button>
              {paginationPageNumbers.map(pageNumber => (
                <button
                  key={pageNumber}
                  className={`bgv-page-nav-action-btn ${currentPage === pageNumber ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              ))}
              <button
                className="bgv-page-nav-action-btn"
                disabled={currentPage === totalPagesCount || totalPagesCount === 0}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPagesCount))}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AllBgvCasesPage;